import { useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SportsTennisIcon from '@mui/icons-material/SportsTennis';
import { useAuth } from '../auth/AuthContext';

export function Login() {
  const { signInWithPassword, requestEmailOtp, verifyEmailOtp, requestPasswordReset, signOut, session, profileError, loading, authLinkError, clearAuthLinkError } = useAuth();
  const [view, setView] = useState<'emailCode' | 'password' | 'forgotPassword' | 'resetEmailSent' | 'invalidRecoveryLink'>(
    authLinkError ? 'invalidRecoveryLink' : 'emailCode',
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const submitPassword = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await signInWithPassword(email.trim(), password);
    } catch (loginError) {
      setError(loginError instanceof Error ? loginError.message : 'Unable to sign in.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitEmailCodeRequest = async (event?: FormEvent) => {
    event?.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setError('Enter a valid email address.');
      return;
    }
    setSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      await requestEmailOtp(normalizedEmail);
      setEmail(normalizedEmail);
      setOtpSent(true);
      setNotice("If this email is associated with CourtCaptain, we've sent you a sign-in link and code (check your spam folder). You can click the link or enter the code below to sign-in.");
    } catch (otpError) {
      setError(otpError instanceof Error ? otpError.message : 'Unable to send a sign-in email.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitEmailCode = async (event: FormEvent) => {
    event.preventDefault();
    const normalizedToken = otpCode.replace(/\D/g, '');
    if (normalizedToken.length !== 6) {
      setError('Enter the 6-digit code from your email, or use the sign-in link from that email.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await verifyEmailOtp(email.trim().toLowerCase(), normalizedToken);
    } catch (otpError) {
      setError(otpError instanceof Error ? otpError.message : 'Unable to verify that sign-in code.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitPasswordReset = async (event: FormEvent) => {
    event.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setError('Enter a valid email address.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await requestPasswordReset(normalizedEmail);
      setView('resetEmailSent');
    } catch {
      setError('Unable to send a reset link right now. Please wait a moment and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const backToLogin = () => {
    setPassword('');
    setOtpCode('');
    setOtpSent(false);
    setError(null);
    setNotice(null);
    clearAuthLinkError();
    setView('emailCode');
  };

  const activeView = authLinkError ? 'invalidRecoveryLink' : view;

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: 'background.default', p: 2 }}>
      <Paper variant="outlined" sx={{ width: '100%', maxWidth: 420, p: 3 }}>
        <Stack spacing={2.5}>
          <Stack spacing={1} alignItems="center" textAlign="center">
            <SportsTennisIcon color="primary" sx={{ fontSize: 44 }} />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>CourtCaptain</Typography>
            <Typography variant="body2" color="text.secondary">
              {activeView === 'forgotPassword'
                ? 'Enter your email to request a password reset.'
                : activeView === 'resetEmailSent'
                  ? 'Check your email for the next step.'
                  : activeView === 'password'
                    ? 'Sign in with your password.'
                    : 'Sign in with an email link or code.'}
            </Typography>
          </Stack>

          {(activeView === 'emailCode' || activeView === 'password') && profileError && <Alert severity="error">{profileError}</Alert>}
          {notice && <Alert severity="success">{notice}</Alert>}
          {error && <Alert severity="error">{error}</Alert>}

          {activeView === 'invalidRecoveryLink' ? (
            <Stack spacing={2}>
              <Alert severity="error">
                {authLinkError || 'This password link is invalid or has expired. Request a new reset link and try again.'}
              </Alert>
              <Button variant="contained" onClick={backToLogin}>Back to Sign In</Button>
            </Stack>
          ) : activeView === 'resetEmailSent' ? (
            <Stack spacing={2}>
              <Alert severity="success">
                If an account exists for that email, a password reset link has been sent.
              </Alert>
              <Button variant="outlined" onClick={backToLogin}>Back to Sign In</Button>
            </Stack>
          ) : activeView === 'forgotPassword' ? (
            <Box component="form" onSubmit={submitPasswordReset}>
              <Stack spacing={2}>
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  autoComplete="email"
                  required
                  autoFocus
                  fullWidth
                />
                <Button type="submit" variant="contained" disabled={submitting} fullWidth>
                  {submitting ? <CircularProgress color="inherit" size={22} /> : 'Send Reset Link'}
                </Button>
                <Button variant="text" disabled={submitting} onClick={backToLogin}>Cancel / Back to Sign In</Button>
              </Stack>
            </Box>
          ) : session && profileError ? (
            <Button variant="outlined" onClick={() => void signOut()}>
              Sign Out
            </Button>
          ) : activeView === 'password' ? (
            <Box component="form" onSubmit={submitPassword}>
              <Stack spacing={2}>
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  autoComplete="email"
                  required
                  fullWidth
                />
                <TextField
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                  required
                  fullWidth
                />
                <Button
                  variant="text"
                  size="small"
                  sx={{ alignSelf: 'flex-end', mt: -1 }}
                  onClick={() => {
                    setError(null);
                    setNotice(null);
                    setView('forgotPassword');
                  }}
                >
                  Forgot password?
                </Button>
                <Button type="submit" variant="contained" disabled={submitting || loading} fullWidth>
                  {submitting ? <CircularProgress color="inherit" size={22} /> : 'Sign In'}
                </Button>
                <Button
                  variant="outlined"
                  disabled={submitting || loading}
                  onClick={() => {
                    setError(null);
                    setNotice(null);
                    setView('emailCode');
                  }}
                >
                  Use Email Code Instead
                </Button>
              </Stack>
            </Box>
          ) : (
            <Box component="form" onSubmit={otpSent ? submitEmailCode : submitEmailCodeRequest}>
              <Stack spacing={2}>
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  autoComplete="email"
                  required
                  fullWidth
                />
                {otpSent && (
                  <TextField
                    label="6-Digit Code"
                    value={otpCode}
                    onChange={(event) => setOtpCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                    autoComplete="one-time-code"
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]{6}', minLength: 6, maxLength: 6 }}
                    required
                    autoFocus
                    fullWidth
                  />
                )}
                <Button type="submit" variant="contained" disabled={submitting || loading} fullWidth>
                  {submitting ? <CircularProgress color="inherit" size={22} /> : otpSent ? 'Verify Code' : 'Email Me a Sign-In Link / Code'}
                </Button>
                {otpSent && (
                  <Button variant="text" disabled={submitting || loading} onClick={() => void submitEmailCodeRequest()}>
                    Send a New Code
                  </Button>
                )}
                <Button
                  variant="outlined"
                  disabled={submitting || loading}
                  onClick={() => {
                    setError(null);
                    setNotice(null);
                    setPassword('');
                    setView('password');
                  }}
                >
                  Sign In with Password
                </Button>
              </Stack>
            </Box>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
